import { Dispatch, SetStateAction } from 'react';
import getLocalStorageKey from 'api/browser/localstorage/get';
import setLocalStorageKey from 'api/browser/localstorage/set';
import { TelemetryFieldKey } from 'api/v5/v5';
import { LOCALSTORAGE } from 'constants/localStorage';
import { defaultOptionsQuery } from 'container/OptionsMenu/constants';
import { FontSize, OptionsQuery } from 'container/OptionsMenu/types';

import { FormattingOptions, PreferenceMode, Preferences } from '../types';

// --- LOGS preferences updater config ---
const getLogsUpdaterConfig = (
	preferences: Preferences | null,
	redirectWithOptionsData: (options: OptionsQuery) => void,
	setSavedViewPreferences: Dispatch<SetStateAction<Preferences | null>>,
): {
	updateColumns: (newColumns: TelemetryFieldKey[], mode: string) => void;
	updateFormatting: (newFormatting: FormattingOptions, mode: string) => void;
} => ({
	updateColumns: (newColumns: TelemetryFieldKey[], mode: string): void => {
		if (mode === PreferenceMode.SAVED_VIEW) {
			setSavedViewPreferences((prev) => {
				if (!prev) {
					return {
						columns: newColumns,
						formatting: {
							maxLines: 1,
							format: 'table',
							fontSize: 'small' as FontSize,
							version: 1,
						},
					};
				}

				return {
					...prev,
					columns: newColumns,
				};
			});
		}

		if (mode === PreferenceMode.DIRECT) {
			// URL options 파라미터는 useOptionsMenu 쪽 리다이렉트와 반드시 같은
			// 형태여야 한다. formatting 스프레드로 version 같은 여분 키가 섞이면
			// safeNavigate의 동일 URL 판정이 깨져 팝오버가 열릴 때마다 리다이렉트가
			// 발생한다(팝오버가 닫히는 버그).
			redirectWithOptionsData({
				...defaultOptionsQuery,
				maxLines: preferences?.formatting?.maxLines ?? defaultOptionsQuery.maxLines,
				format: preferences?.formatting?.format ?? defaultOptionsQuery.format,
				fontSize: preferences?.formatting?.fontSize ?? defaultOptionsQuery.fontSize,
				selectColumns: newColumns,
			});

			// Also update local storage
			const local = JSON.parse(
				getLocalStorageKey(LOCALSTORAGE.LOGS_LIST_OPTIONS) || '{}',
			);
			local.selectColumns = newColumns;
			setLocalStorageKey(LOCALSTORAGE.LOGS_LIST_OPTIONS, JSON.stringify(local));
		}
	},
	updateFormatting: (newFormatting: FormattingOptions, mode: string): void => {
		if (mode === PreferenceMode.SAVED_VIEW) {
			setSavedViewPreferences((prev) => {
				if (!prev) {
					return { columns: [], formatting: newFormatting };
				}
				return {
					...prev,
					formatting: newFormatting,
				};
			});
		}

		if (mode === PreferenceMode.DIRECT) {
			// selectColumns까지 채워야 한다. defaultOptionsQuery의 빈 배열이 그대로
			// URL에 실리면 컬럼 선택이 URL에서 지워져 다른 경로와 계속 어긋난다.
			redirectWithOptionsData({
				...defaultOptionsQuery,
				maxLines:
					newFormatting.maxLines ??
					preferences?.formatting?.maxLines ??
					defaultOptionsQuery.maxLines,
				format:
					newFormatting.format ??
					preferences?.formatting?.format ??
					defaultOptionsQuery.format,
				fontSize:
					newFormatting.fontSize ??
					preferences?.formatting?.fontSize ??
					defaultOptionsQuery.fontSize,
				selectColumns: preferences?.columns ?? defaultOptionsQuery.selectColumns,
			});

			// Also update local storage
			const local = JSON.parse(
				getLocalStorageKey(LOCALSTORAGE.LOGS_LIST_OPTIONS) || '{}',
			);
			Object.assign(local, newFormatting);
			setLocalStorageKey(LOCALSTORAGE.LOGS_LIST_OPTIONS, JSON.stringify(local));
		}
	},
});

export default getLogsUpdaterConfig;
